import { NEW_MASCOTS } from '@daymade/common-lib/constants';
import clsx from 'clsx';
import AbTestVariantPicker from 'components/ab-test/AbTestVariantPicker';
import Button from 'components/common/Button';
import GenericModal from 'components/common/GenericModal';
import HeaderOrderSummary from 'components/enter2026/HeaderOrderSummary';
import ImageWithFallback from 'components/ImageWithFallback';
import { useCtaButtonTextAndHref } from 'hooks/cta/useCtaButtonTextAndHref';
import { useUIStore } from 'hooks/stores/useUIStore';
import { useUserStore } from 'hooks/stores/useUserStore';
import { useHeaderContent } from 'hooks/ui/useHeaderContent';
import { useIsMobile } from 'hooks/ui/useIsMobile';
import { observer } from 'mobx-react-lite';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import {
  clearAbTestMenuFlag,
  hasAbTestMenuFlag,
  readAbTestMenuQueryParam,
  setAbTestMenuFlag,
} from 'utils/ab-test/abTestMenuFlag';
import {
  ASSET_PREFIX,
  useChristmasLogos,
  useHalloweenLogos,
} from 'utils/constants';

import Logo from '../../../components/svg/Logo/Logo.svg';
import Chevron from '../../svg/24x24/ChevronRight.svg';
import AnnouncementBanners from '../header/AnnouncementBanners';
import {
  actionItem,
  breakItem,
  isActionItem,
  isBreakItem,
  isLinkItem,
  isSubpagesItem,
  linkItem,
  loggedInMenu,
  loggedOutMenu,
  NAVIGATION_CSS_TRANSITION_MILLIS,
  subpageItem,
} from './constants';
import styles from './header2026.module.css';
import MenuAnnouncement from './menu-announcement';

export const DaymadeLogo = ({
  logoColor = 'grey',
  isChristmas,
  isHalloween,
}: {
  logoColor?: 'grey' | 'black';
  isChristmas?: boolean;
  isHalloween?: boolean;
}) => (
  <Link href="/?redirect=false" style={{ textAlign: 'left' }}>
    {isChristmas || isHalloween ? (
      <img
        src={`${ASSET_PREFIX}/images/daymade-logo${isChristmas ? '-xmas' : ''}${
          isHalloween ? '-halloween' : ''
        }.png`}
        height="40"
        className={styles.logoImage}
        alt="DAYMADE seasonal logo"
      />
    ) : (
      <div
        className={clsx(
          styles.logoImageWrapper,
          logoColor && styles[`logoColor__${logoColor}`],
        )}
      >
        <Logo />
      </div>
    )}
  </Link>
);

const BurgerMenuComponent = ({
  menuOpen,
  isLoggedIn,
  toggleNavigation,
  claimablePrizesCount,
}: {
  menuOpen: boolean;
  isLoggedIn: boolean;
  toggleNavigation: () => void;
  claimablePrizesCount?: number;
}) => (
  <div
    className={clsx(styles.burgerMenu, !isLoggedIn && styles.burgerLoggedOut)}
  >
    <button
      type="button"
      aria-label={menuOpen ? 'Close menu' : 'Open menu'}
      aria-expanded={menuOpen}
      className={clsx(styles.menuAction, menuOpen ? styles.burgerOpen : '')}
      onClick={toggleNavigation}
    >
      <span className={clsx(styles.menuBar, styles.menuBarTop)}></span>
      <span className={clsx(styles.menuBar, styles.menuBarMiddle)}></span>
      <span className={clsx(styles.menuBar, styles.menuBarBottom)}></span>
      {claimablePrizesCount && claimablePrizesCount > 0 ? (
        <div
          className={clsx(styles.notifyDot, menuOpen ? styles.hideDot : '')}
        />
      ) : null}
    </button>
  </div>
);

interface HeaderProps {
  hideOnMobile?: boolean;
  clearHeader?: boolean;
  displayOnScroll?: boolean;
}

const Header = observer(
  ({ hideOnMobile, clearHeader, displayOnScroll }: HeaderProps) => {
    const { isMobile } = useIsMobile();
    const { headerBanner } = useUIStore();
    const { showOrderSummary } = useHeaderContent();
    const userStore = useUserStore();
    const {
      activeSubscriptions,
      balances,
      claimablePrizesCount,
      currentUser,
      isInMonthlyRaffle,
      isOperator,
      logout,
    } = userStore;
    const enterCta = useCtaButtonTextAndHref();

    const [isChristmas] = useState(useChristmasLogos());
    const [isHalloween] = useState(useHalloweenLogos());
    const [navOpen, setNavOpen] = useState(false);
    const [navVisible, setNavVisible] = useState(false);
    const [menuItems, setMenuItems] =
      useState<(linkItem | actionItem | subpageItem | breakItem)[]>();
    const [openSubpageMenu, setOpenSubpageMenu] = useState(false);
    const [selectedSubpage, setSelectedSubpage] = useState<{
      title: string;
      pages: linkItem[];
    }>();
    const [isScrollingUp, setIsScrollingUp] = useState(false);
    const [isAtTop, setIsAtTop] = useState(true);
    const [showAbTestPicker, setShowAbTestPicker] = useState(false);
    const [showAbTestMenu, setShowAbTestMenu] = useState(false);
    const lastScrollYRef = useRef(0);
    const ticking = useRef(false);
    const FADE_THRESHOLD = 1;

    useEffect(() => {
      if (currentUser && currentUser.emailAddress) {
        setMenuItems(loggedInMenu);
      } else {
        setMenuItems(loggedOutMenu);
      }
    }, [currentUser]);

    // The flag keeps the menu available after an operator logs out, and localStorage is only readable after mount
    useEffect(() => {
      const queryParam = readAbTestMenuQueryParam();
      if (queryParam === false) {
        clearAbTestMenuFlag();
        setShowAbTestMenu(isOperator);
        return;
      }
      if (isOperator || queryParam) {
        setAbTestMenuFlag();
      }
      setShowAbTestMenu(isOperator || queryParam || hasAbTestMenuFlag());
    }, [isOperator]);

    useEffect(() => {
      const body = document.body;
      if (navOpen) {
        body.style = 'height: 100vh; overflow-y: hidden;';
      } else {
        body.style = '';
      }
    }, [navOpen]);

    // Scroll handler for hide-on-scroll functionality
    useEffect(() => {
      if (!displayOnScroll) return;

      const handleScroll = () => {
        if (!ticking.current) {
          window.requestAnimationFrame(() => {
            const currentScrollY = window.scrollY;
            const isScrollUp = currentScrollY < lastScrollYRef.current;
            const atTop = currentScrollY < FADE_THRESHOLD;

            setIsScrollingUp(isScrollUp);
            setIsAtTop(atTop);

            lastScrollYRef.current = currentScrollY;
            ticking.current = false;
          });

          ticking.current = true;
        }
      };

      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }, [displayOnScroll]);

    const toggleNavigation = () => {
      if (navOpen) {
        setNavOpen(false);
        setTimeout(
          () => setNavVisible(false),
          NAVIGATION_CSS_TRANSITION_MILLIS,
        );
      } else {
        setNavVisible(true);
        // Force Safari to paint the initial state before transitioning
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setNavOpen(true);
          });
        });
      }
    };

    const handleCloseExpandedMenu = () => {
      setNavOpen(false);

      setTimeout(() => {
        setOpenSubpageMenu(false);
        setNavVisible(false);
      }, NAVIGATION_CSS_TRANSITION_MILLIS);
    };

    const openSubpage = (page: string, subpages: linkItem[]) => {
      setSelectedSubpage({
        title: page,
        pages: subpages,
      });
      setOpenSubpageMenu(true);
    };

    const runAction = (action: string) => {
      switch (action) {
        case 'logout': {
          logout();
          break;
        }
        case 'ab-tests': {
          handleCloseExpandedMenu();
          setShowAbTestPicker(true);
          break;
        }
      }
    };

    const ChooseYourPlanButton = ({ text = 'Get Started' }) => (
      <Button
        href={enterCta.href}
        onClick={() => {
          enterCta.onClick();
          handleCloseExpandedMenu();
        }}
      >
        {text}
      </Button>
    );

    return (
      <>
        <div
          className={clsx(
            'daymadeTypographyReset',
            styles.headerContainer,
            hideOnMobile ? styles.hideOnMobile : '',
            clearHeader ? styles.clearHeader : '',
            displayOnScroll && styles.scrollHideEnabled,
            displayOnScroll && isScrollingUp && styles.headerScrollingUp,
            displayOnScroll && isAtTop ? styles.headerAtTop : '',
            navOpen ? styles.scrollHideEnabledNavOpen : '',
          )}
        >
          <AnnouncementBanners headerBanner={headerBanner} />

          <div className={styles.background}>
            <div className={styles.content}>
              <div className={clsx(styles.barLinks, styles.desktop)}>
                <div className={styles.leftBarLinks}>
                  <DaymadeLogo
                    isChristmas={isChristmas}
                    isHalloween={isHalloween}
                  />
                </div>

                <div className={styles.rightBarLinks}>
                  {/* MOBILE ONLY PAYMENT SUMMARY - ENTER FLOW */}
                  {showOrderSummary && isMobile ? (
                    <div
                      className={clsx(
                        'daymadeTypographyReset',
                        styles.orderSummaryContainer,
                      )}
                    >
                      <HeaderOrderSummary />
                    </div>
                  ) : (
                    <BurgerMenuComponent
                      menuOpen={navOpen}
                      isLoggedIn={Boolean(currentUser)}
                      claimablePrizesCount={claimablePrizesCount}
                      toggleNavigation={toggleNavigation}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* MAIN MENU */}
        <div
          className={clsx(
            styles.menu,
            navOpen && styles.menuOpen,
            !navVisible && styles.menuHidden,
            currentUser || headerBanner ? styles.menuLoggedIn : '',
          )}
        >
          <div className={styles.menuContent}>
            <div
              className={clsx(
                styles.menuHeader,
                currentUser || headerBanner ? styles.menuHeaderWithUser : '',
              )}
            >
              {currentUser && (
                <Link
                  href="/account"
                  className={styles.menuUser}
                  onClick={handleCloseExpandedMenu}
                >
                  <div className={styles.avatar}>
                    <ImageWithFallback
                      src={currentUser?.avatarLocation || ''}
                      fallbackSrc={NEW_MASCOTS[1]}
                      alt="User profile icon"
                      layout="fill"
                      objectFit="cover"
                    />
                  </div>
                  <div className={styles.userInfo}>
                    <div
                      className={clsx(
                        'daymadeTypographyMMedium',
                        styles.userName,
                      )}
                    >
                      {currentUser?.firstName}
                    </div>
                    <div
                      className={clsx(
                        'daymadeTypographySMedium',
                        styles.userEmail,
                      )}
                    >
                      {currentUser?.emailAddress}
                    </div>
                  </div>
                </Link>
              )}
              {!currentUser && headerBanner ? (
                <div className={styles.menuOpenDaymadeLogo}>
                  <DaymadeLogo />
                </div>
              ) : null}
              <button
                type="button"
                aria-label="Close menu"
                className={clsx(
                  styles.menuClose,
                  (currentUser && currentUser.id) || headerBanner
                    ? styles.menuCloseLoggedIn
                    : '',
                )}
                onClick={handleCloseExpandedMenu}
              >
                <span
                  className={clsx(styles.menuBar, styles.menuCloseOne)}
                ></span>
                <span
                  className={clsx(styles.menuBar, styles.menuCloseTwo)}
                ></span>
              </button>
            </div>
            <div className={styles.menuItemsWrapper}>
              {menuItems && menuItems.length > 0 ? (
                <div className={styles.menuItems}>
                  {menuItems.map((item, i) => {
                    if (isLinkItem(item)) {
                      if (item.condition === 'admin-only' && !isOperator)
                        return;

                      return (
                        <Link href={item.link} key={i}>
                          <div
                            className={clsx(styles.menuItem)}
                            onClick={handleCloseExpandedMenu}
                          >
                            <div className="daymadeTypographyMMedium">
                              {item.title}
                            </div>
                            {item?.counter?.type === 'tokens' &&
                            balances?.tokenBalance &&
                            balances?.tokenBalance > 0 ? (
                              <div
                                className={clsx(
                                  styles.counter,
                                  styles[`counter--${item.counter.colour}`],
                                  'daymadeTypographySMedium',
                                )}
                              >
                                {balances?.tokenBalance}
                              </div>
                            ) : null}
                            {item?.counter?.type === 'wins' &&
                              claimablePrizesCount > 0 && (
                                <div
                                  className={clsx(
                                    styles.counter,
                                    styles[`counter--${item.counter.colour}`],
                                    'daymadeTypographySMedium',
                                  )}
                                >
                                  {claimablePrizesCount}
                                </div>
                              )}
                          </div>
                        </Link>
                      );
                    }

                    if (isSubpagesItem(item)) {
                      return (
                        <button
                          key={i}
                          type="button"
                          onClick={() => openSubpage(item.title, item.subpages)}
                        >
                          <div className={styles.menuItem}>
                            <span className="daymadeTypographyMMedium">
                              {item.title}
                            </span>
                            <Chevron />
                          </div>
                        </button>
                      );
                    }

                    if (isActionItem(item)) {
                      if (item.condition === 'ab-test-tools' && !showAbTestMenu)
                        return;

                      return (
                        <button
                          key={i}
                          type="button"
                          onClick={() => runAction(item.action)}
                        >
                          <div
                            className={clsx(
                              'daymadeTypographyMMedium',
                              styles.menuItem,
                            )}
                          >
                            {item.title}
                          </div>
                        </button>
                      );
                    }

                    if (isBreakItem(item)) {
                      return (
                        <div key={i} className={clsx(styles.menuBreak)}></div>
                      );
                    }
                  })}
                </div>
              ) : null}
              <div
                className={clsx(
                  styles.subpage,
                  openSubpageMenu ? styles.subpageOpen : '',
                )}
              >
                <button type="button" onClick={() => setOpenSubpageMenu(false)}>
                  <div
                    className={clsx(
                      'daymadeTypographyMMedium',
                      styles.menuItem,
                      styles.menuItemSubpageTitle,
                    )}
                  >
                    <Chevron />
                    {selectedSubpage?.title}
                  </div>
                </button>
                {selectedSubpage?.pages.map((item, i) => {
                  if (
                    item.condition === 'monthly-10k' &&
                    activeSubscriptions.length === 0 &&
                    !isInMonthlyRaffle
                  ) {
                    return;
                  }

                  if (
                    item.condition === 'referrals' &&
                    activeSubscriptions.length === 0
                  ) {
                    return;
                  }

                  if (item.condition === 'off') return;

                  return (
                    <Link href={item.link} key={i}>
                      <div
                        className={clsx(
                          'daymadeTypographyMMedium',
                          styles.menuItem,
                        )}
                        onClick={handleCloseExpandedMenu}
                      >
                        <div>{item.title}</div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
            <div className={styles.menuFooter}>
              {!currentUser && (
                <div className={styles.footerAuthButtons}>
                  <ChooseYourPlanButton />
                  <Button
                    href="/login"
                    onClick={handleCloseExpandedMenu}
                    buttonStyle="Secondary"
                  >
                    Login
                  </Button>
                </div>
              )}

              {currentUser && activeSubscriptions.length === 0 && (
                <div className={styles.footerEnterButton}>
                  <ChooseYourPlanButton text="Enter Now" />
                </div>
              )}

              <MenuAnnouncement />
            </div>
          </div>
        </div>
        <div
          onClick={handleCloseExpandedMenu}
          className={clsx(
            styles.dimmedBackground,
            navOpen ? styles.showDimmedBackground : styles.hideDimmedBackground,
          )}
        />
        {showAbTestPicker && (
          <GenericModal onClose={() => setShowAbTestPicker(false)}>
            <AbTestVariantPicker />
          </GenericModal>
        )}
      </>
    );
  },
);

export default Header;
